'use client'

import { useState } from 'react'
import {
  IconWifi,
  IconLoader2,
  IconDatabase,
  IconTrash,
  IconCheck,
  IconDownload,
  IconBook,
  IconUserCircle
} from '@tabler/icons-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { MutedText } from '@/components/ui/typography'
import { Badge } from '@/components/ui/badge'
import { useTranslations } from 'next-intl'
import { useLocale } from 'next-intl'
import { useOfflineStories } from '@/hooks/useOfflineStories'
import { Link } from '@/i18n/routing'
import { stories as allStories } from '@/content/story-metadata'
import { LoginDialog } from '@/components/auth/login-dialog'

interface TierInfo {
  file: string
  entryCount: number
  sizeBytes: number
  checksum: string
}

interface Props {
  isOfflineMode: boolean
  hasOfflineData: boolean
  downloadProgress: number | null
  statusText: string
  isReady: boolean
  onToggle: () => void
  onDelete: () => void
  onRedownload: () => void
  currentVersion: string | null
  latestVersion: string | null
  isUpdateAvailable: boolean
  localFile: {
    size: number
    lastModified: number
    entryCount?: number | null
  } | null
  autoUpdateEnabled: boolean
  onToggleAutoUpdate: (value: boolean) => void
  isSupported: boolean
  supportMessage?: string | null
  offlineError?: string | null
  manifestTiers?: { premium?: TierInfo }
  currentTier?: 'free' | 'premium' | null
  className?: string
  isAuthenticated?: boolean
}

export function OfflineStatus({
  isOfflineMode,
  hasOfflineData,
  downloadProgress,
  statusText,
  isReady,
  onToggle,
  onDelete,
  onRedownload,
  currentVersion,
  latestVersion,
  isUpdateAvailable,
  localFile,
  autoUpdateEnabled,
  onToggleAutoUpdate,
  isSupported,
  supportMessage,
  offlineError,
  manifestTiers,
  currentTier,
  className,
  isAuthenticated = false
}: Props) {
  const t = useTranslations('offline')
  const tCommon = useTranslations('common')
  const locale = useLocale()
  const { savedSlugs, removeStory } = useOfflineStories(locale)
  const isDownloading = downloadProgress !== null
  const controlsDisabled = !isSupported || isDownloading
  const [loginDialogOpen, setLoginDialogOpen] = useState(false)
  const downloadedEntries = localFile?.entryCount ?? null

  return (
    <div
      className={cn(
        'bg-card text-card-foreground flex w-full flex-col gap-4 rounded-xl shadow-sm',
        className
      )}
    >
      {/* Header with toggle */}
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="flex min-w-0 flex-1 items-start gap-3">
          <div className="bg-muted/70 shrink-0 rounded-full p-2 transition-colors">
            {isDownloading ? (
              <IconLoader2 className="text-primary h-5 w-5 animate-spin" />
            ) : isOfflineMode && isReady ? (
              <IconDatabase className="h-5 w-5 text-emerald-400 dark:text-emerald-400" />
            ) : (
              <IconWifi className="h-5 w-5 text-blue-500 dark:text-blue-400" />
            )}
          </div>

          <div className="flex min-w-0 flex-1 flex-col gap-1 overflow-hidden">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-foreground truncate text-sm font-semibold">
                {t('offlineDictionary')}
              </span>
              <Badge
                variant={isOfflineMode ? 'outline' : 'secondary'}
                className="text-[10px] uppercase"
              >
                {isOfflineMode ? t('enabled') : t('onlineOnly')}
              </Badge>
            </div>
            <MutedText className="text-xs">{t('offlineDescription')}</MutedText>
          </div>
        </div>

        <div className="bg-muted/40 text-muted-foreground flex flex-col gap-2 rounded-xl p-3 text-xs tracking-wide uppercase lg:w-[240px]">
          <div className="flex items-center justify-between gap-3">
            <span>{t('offlineMode')}</span>
            <Switch
              checked={isOfflineMode}
              onCheckedChange={() => onToggle()}
              disabled={controlsDisabled}
              aria-label={
                isOfflineMode ? t('switchToOnline') : t('switchToOffline')
              }
            />
          </div>
          <div className="flex items-center justify-between gap-3">
            <span>{t('autoUpdate')}</span>
            <Switch
              checked={autoUpdateEnabled}
              onCheckedChange={onToggleAutoUpdate}
              disabled={!isOfflineMode || controlsDisabled}
              aria-label={t('toggleAutoUpdate')}
            />
          </div>
        </div>
      </div>

      {/* Browser support warning */}
      {!isSupported && (
        <div className="rounded-lg bg-amber-500/10 p-3 text-sm text-amber-900 dark:bg-amber-900/20 dark:text-amber-100">
          {supportMessage || t('notSupported')}
        </div>
      )}
      {offlineError && (
        <div className="bg-destructive/10 text-destructive rounded-lg p-3 text-sm">
          {offlineError}
        </div>
      )}
      {!isAuthenticated && (
        <div className="border-border/70 bg-muted/30 flex flex-col gap-3 rounded-xl border p-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex min-w-0 items-start gap-3">
            <IconUserCircle className="text-muted-foreground mt-0.5 h-5 w-5 shrink-0" />
            <div className="space-y-1">
              <p className="text-sm font-medium">Offline dictionary is free.</p>
              <MutedText className="text-xs">
                Downloads and offline story saves work without an account. Sign
                in for your learning data and Pro stories.
              </MutedText>
            </div>
          </div>
          <LoginDialog open={loginDialogOpen} onOpenChange={setLoginDialogOpen}>
            <Button
              size="sm"
              variant="outline"
              className="shrink-0"
              onClick={() => setLoginDialogOpen(true)}
            >
              {tCommon('signIn')}
            </Button>
          </LoginDialog>
        </div>
      )}
      {/* Single complete dictionary download. Legacy lite files are upgraded. */}
      {isSupported && (
        <div>
          <TierOption
            label={t('tierComplete')}
            description="Full dictionary database for serious offline study."
            entryCount={manifestTiers?.premium?.entryCount ?? 0}
            sizeBytes={manifestTiers?.premium?.sizeBytes ?? 0}
            isDownloaded={currentTier === 'premium' && hasOfflineData}
            isDownloading={isDownloading}
            isDisabled={controlsDisabled}
            hasUpdate={
              isUpdateAvailable || (currentTier === 'free' && hasOfflineData)
            }
            onDownload={onRedownload}
            onDelete={onDelete}
            t={t}
          />
        </div>
      )}

      {/* Download progress */}
      {isDownloading && (
        <div className="text-sm">
          <div className="flex items-center gap-2">
            <IconLoader2 className="h-4 w-4 animate-spin" />
            <span>
              {statusText} {Math.round(downloadProgress)}%
            </span>
          </div>
          <div className="bg-muted mt-2 h-1.5 w-full overflow-hidden rounded-full">
            <div
              className="bg-primary h-full transition-all duration-300"
              style={{ width: `${downloadProgress}%` }}
            />
          </div>
        </div>
      )}

      {/* Version info */}
      {hasOfflineData && !isDownloading && (
        <div className="text-muted-foreground text-xs">
          <span>
            {t('localVersion', { version: currentVersion || 'unknown' })}
            {localFile && ` · ${formatBytes(localFile.size)}`}
            {downloadedEntries &&
              ` · ${downloadedEntries.toLocaleString()} entries`}
            {latestVersion &&
              currentVersion &&
              latestVersion !== currentVersion &&
              ` · Latest: ${latestVersion}`}
          </span>
        </div>
      )}

      {/* Saved Stories Section */}
      <div className="border-t pt-4">
        <div className="mb-3 flex items-center gap-2">
          <IconBook className="text-muted-foreground h-4 w-4" />
          <span className="text-sm font-medium">{t('savedStories')}</span>
          <span className="text-muted-foreground text-xs">
            {t('savedStoriesUnlimited', { count: savedSlugs.length })}
          </span>
        </div>

        {savedSlugs.length === 0 ? (
          <MutedText className="text-xs">{t('noSavedStories')}</MutedText>
        ) : (
          <div className="flex flex-col gap-1">
            {savedSlugs.map((slug) => {
              const story = allStories.find((s) => s.slug === slug)
              if (!story) return null
              return (
                <div
                  key={slug}
                  className="bg-muted/40 flex items-center justify-between gap-2 rounded-lg px-3 py-2"
                >
                  <Link
                    href={`/read/${slug}`}
                    className="min-w-0 flex-1 truncate text-sm hover:underline"
                  >
                    {story.simplifiedTitle}
                  </Link>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => removeStory(slug)}
                    className="text-destructive hover:text-destructive h-7 w-7 shrink-0 p-0"
                  >
                    <IconTrash className="h-3.5 w-3.5" />
                  </Button>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}

// Tier option component
interface TierOptionProps {
  label: string
  description: string
  entryCount: number
  sizeBytes: number
  isDownloaded: boolean
  isDownloading: boolean
  isDisabled: boolean
  hasUpdate?: boolean
  onDownload: () => void
  onDelete: () => void
  t: (key: string) => string
}

function TierOption({
  label,
  description,
  entryCount,
  sizeBytes,
  isDownloaded,
  isDownloading,
  isDisabled,
  hasUpdate,
  onDownload,
  onDelete,
  t
}: TierOptionProps) {
  const sizeMB = (sizeBytes / 1024 / 1024).toFixed(1)

  // Determine button label
  const buttonLabel = hasUpdate
    ? t('update')
    : isDownloaded
      ? t('redownload')
      : t('download')

  return (
    <div
      className={cn(
        'flex min-h-[132px] flex-col justify-between gap-4 rounded-xl border p-4 transition-colors',
        isDownloaded && 'border-emerald-500/50 bg-emerald-500/5',
        hasUpdate && isDownloaded && 'border-amber-500/50 bg-amber-500/5',
        isDisabled && 'opacity-60'
      )}
    >
      <div className="flex flex-col gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium">{label}</span>
          {isDownloaded && !hasUpdate && (
            <Badge
              variant="outline"
              className="border-emerald-500/30 text-[10px] text-emerald-600"
            >
              <IconCheck className="mr-1 h-3 w-3" />
              {t('downloaded')}
            </Badge>
          )}
          {hasUpdate && (
            <Badge
              variant="outline"
              className="border-amber-500/30 text-[10px] text-amber-600"
            >
              {t('updateAvailable')}
            </Badge>
          )}
        </div>
        <MutedText className="text-xs">{description}</MutedText>
        <span className="text-muted-foreground text-xs">
          {entryCount.toLocaleString()} {t('entries')} · {sizeMB} MB
        </span>
      </div>

      <div className="flex items-center gap-1.5">
        {isDownloaded && !hasUpdate && (
          <Button
            size="sm"
            variant="ghost"
            onClick={onDelete}
            disabled={isDownloading}
            className="text-destructive h-8 px-2"
          >
            <IconTrash className="h-4 w-4" />
          </Button>
        )}
        <Button
          size="sm"
          variant={hasUpdate ? 'default' : isDownloaded ? 'ghost' : 'outline'}
          onClick={onDownload}
          disabled={isDownloading || isDisabled}
          className="min-w-0 flex-1 shrink-0"
        >
          <IconDownload className="mr-1.5 h-4 w-4" />
          {buttonLabel}
        </Button>
      </div>
    </div>
  )
}

function formatBytes(bytes: number) {
  if (!bytes) return '0 B'
  const units = ['B', 'KB', 'MB', 'GB']
  const exponent = Math.min(
    Math.floor(Math.log(bytes) / Math.log(1024)),
    units.length - 1
  )
  const value = bytes / Math.pow(1024, exponent)
  return `${value.toFixed(value >= 10 ? 0 : 1)} ${units[exponent]}`
}
